import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_PAGE_SIZE = 5;

interface UsePaginationOptions {
  pageSize?: number;
}

interface UsePaginationResult<T> {
  pageItems: T[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  setPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  startIndex: number;
  endIndex: number;
}

export function usePagination<T>(
  items: T[],
  options: UsePaginationOptions = {},
): UsePaginationResult<T> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const [currentPage, setCurrentPage] = useState(1);
  const prevLengthRef = useRef(items.length);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    if (items.length !== prevLengthRef.current) {
      prevLengthRef.current = items.length;
      const nextTotalPages = Math.max(1, Math.ceil(items.length / pageSize));
      setCurrentPage((p) => Math.min(p, nextTotalPages));
    }
  }, [items.length, pageSize]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const pageItems = useMemo(
    () => items.slice(startIndex, endIndex),
    [items, startIndex, endIndex],
  );

  const setPage = useCallback(
    (page: number) => {
      const clamped = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(clamped);
    },
    [totalPages],
  );

  const nextPage = useCallback(
    () => setPage(currentPage + 1),
    [currentPage, setPage],
  );
  const prevPage = useCallback(
    () => setPage(currentPage - 1),
    [currentPage, setPage],
  );

  return {
    pageItems,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    setPage,
    nextPage,
    prevPage,
    canGoNext: currentPage < totalPages,
    canGoPrev: currentPage > 1,
    startIndex,
    endIndex,
  };
}

interface UseServerPaginationOptions {
  page: number;
  total: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
}

/** Controlled variant for server-paginated data: page/total live in caller state. */
export function useServerPagination(
  options: UseServerPaginationOptions,
): Omit<UsePaginationResult<never>, "pageItems"> {
  const { page, total, onPageChange } = options;
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  const setPage = useCallback(
    (p: number) => onPageChange(Math.max(1, Math.min(p, totalPages))),
    [onPageChange, totalPages],
  );
  const nextPage = useCallback(
    () => setPage(currentPage + 1),
    [currentPage, setPage],
  );
  const prevPage = useCallback(
    () => setPage(currentPage - 1),
    [currentPage, setPage],
  );

  return {
    currentPage,
    totalPages,
    totalItems: total,
    pageSize,
    setPage,
    nextPage,
    prevPage,
    canGoNext: currentPage < totalPages,
    canGoPrev: currentPage > 1,
    startIndex,
    endIndex,
  };
}
